import axios from 'axios';

const CAPTURE_API_BASE_URL = process.env.REACT_APP_CAPTURE_API_BASE_URL || '';

// Note: Authorization header is injected automatically by the global axios
// interceptor in services/api.js (Supabase token injection).

export async function aiBuildTemplate(captureDocumentId, options = {}) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/ai-build`;
  return (await axios.post(url, {
    captureDocumentId,
    templateName: options.templateName || undefined,
    documentType: options.documentType || 'invoice',
  })).data;
}

/**
 * Build a semantic template from existing Claude extraction results.
 * No additional AI call needed — uses anchor text discovered from OCR words.
 */
export async function buildTemplateFromExtraction(captureDocumentId, options = {}) {
  const { templateName, documentType = 'invoice' } = options;
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/build-from-extraction`;
  return (await axios.post(url, {
    captureDocumentId,
    documentType,
    templateName,
  })).data;
}

/**
 * Create a blank template with no AI-generated fields/zones.
 * Used for the "Build Manually" workflow.
 */
export async function createBlankTemplate(captureDocumentId, { templateName, documentType = 'invoice' } = {}) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates`;
  const response = await axios.post(url, {
    name: templateName || `Manual Template ${new Date().toISOString().slice(0, 10)}`,
    document_type: documentType,
    source_document_id: captureDocumentId,
  });
  return response.data;
}

export async function getTemplateByDocument(documentId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/by-document/${documentId}`;
  return (await axios.get(url)).data;
}

export async function getTemplateFull(templateId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/full`;
  return (await axios.get(url)).data;
}

export async function updateZone(templateId, zoneId, zoneData) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/zones/${zoneId}`;
  return (await axios.put(url, zoneData)).data;
}

export async function getFieldTransforms(templateId, fieldId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/fields/${fieldId}/transforms`;
  return (await axios.get(url)).data;
}

export async function updateFieldTransforms(templateId, fieldId, transforms) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/fields/${fieldId}/transforms`;
  return (await axios.put(url, { transforms })).data;
}

export async function createZone(templateId, zoneData) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/zones`;
  return (await axios.post(url, zoneData)).data;
}

export async function deleteZone(templateId, zoneId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/zones/${zoneId}`;
  return (await axios.delete(url)).data;
}

/**
 * POST that turns a server refusal ({ ok: false, code, message } on 400, 404,
 * 409 or 422) into an Error carrying that code, status and message, so the
 * caller can tell refusals apart. Any other failure is rethrown as axios threw it.
 */
async function postOrRefusal(url, body) {
  try {
    return (await axios.post(url, body)).data;
  } catch (err) {
    const data = err?.response?.data;
    if (data && data.ok === false && typeof data.code === 'string') {
      const refusal = new Error(data.message || data.error || data.code);
      refusal.code = data.code;
      refusal.status = err.response.status;
      refusal.refusal = true;
      throw refusal;
    }
    throw err;
  }
}

/**
 * The label next to the value under a box on the template's sample. Read only.
 * Resolves { ok, value, label, alternatives, confidence }; a refusal throws
 * with .code (no_words_under_box, table_cell, no_label_found, ...).
 */
export async function resolveLabel(templateId, { page_index, box, field_id } = {}) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/zones/resolve-label`;
  return postOrRefusal(url, { page_index, box, ...(field_id ? { field_id } : {}) });
}

/**
 * Create a field's value zone and anchor zone from one box, in one server
 * write. label_text is sent only when the user picked an alternative or typed
 * one. Resolves { ok, value_zone, anchor_zone, label, alternatives, confidence };
 * a refusal throws with .code (field_has_zone, label_rejected, ...).
 *
 * position_only: true creates the value zone alone (no label), and never sends
 * label_text. Resolves { ok, value_zone, anchor_zone: null, position_only: true };
 * refusals include position_only_floating and position_only_not_needed.
 */
export async function createFieldOneBox(templateId, fieldId, { page_index, box, label_text, position_only } = {}) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/fields/${fieldId}/one-box`;
  if (position_only) return postOrRefusal(url, { page_index, box, position_only: true });
  return postOrRefusal(url, { page_index, box, ...(label_text ? { label_text } : {}) });
}

export async function createField(templateId, fieldData) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/fields`;
  return (await axios.post(url, fieldData)).data;
}

export async function updateField(templateId, fieldId, fieldData) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/fields/${fieldId}`;
  return (await axios.patch(url, fieldData)).data;
}

export async function deleteField(templateId, fieldId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/fields/${fieldId}`;
  return (await axios.delete(url)).data;
}

export async function patchZone(templateId, zoneId, patchData) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/zones/${zoneId}`;
  return (await axios.put(url, patchData)).data;
}

export async function saveTemplate(templateId, { name, notes, source_document_id, document_type, page_selection } = {}) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/save`;
  const body = { name, notes, source_document_id, document_type };
  // undefined = "leave the column alone"; null = "store NULL (all pages)".
  // JSON.stringify drops undefined keys and keeps null ones, which is exactly
  // the distinction the save route relies on — so only add the key when the
  // caller actually passed one.
  if (page_selection !== undefined) body.page_selection = page_selection;
  return (await axios.post(url, body)).data;
}

/**
 * B-3: zone-coverage preview for the page-selection control. Grammar checking
 * and the 1/0-index comparison both live server-side — the UI never parses the
 * selection, it just renders this endpoint's answer.
 */
export async function previewPageSelection(templateId, selection) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/page-selection/preview`;
  return (await axios.get(url, { params: { selection: selection ?? '' } })).data;
}

export async function publishTemplate(templateId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/publish`;
  return (await axios.post(url)).data;
}

export async function unpublishTemplate(templateId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/unpublish`;
  return (await axios.post(url)).data;
}

export async function getTemplateHash(templateId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/hash`;
  return (await axios.get(url)).data;
}

export async function detectRegions(templateId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/detect-regions`;
  return (await axios.post(url)).data;
}

export async function createDefaultRegions(templateId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/create-default-regions`;
  return (await axios.post(url)).data;
}

export async function testRunTemplate(templateId, { documentId } = {}) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/test-run`;
  return (await axios.post(url, { documentId: documentId || undefined })).data;
}

// ── Template Identifiers ──

export async function getIdentifiers(templateId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/identifiers`;
  return (await axios.get(url)).data;
}

export async function createIdentifier(templateId, data) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/identifiers`;
  return (await axios.post(url, data)).data;
}

export async function updateIdentifier(templateId, identifierId, data) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/identifiers/${identifierId}`;
  return (await axios.put(url, data)).data;
}

export async function deleteIdentifier(templateId, identifierId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/identifiers/${identifierId}`;
  return (await axios.delete(url)).data;
}

// ── Line-item table columns (Stage-1 Unit 3a backend) ──

// Auto-propose columns for a drawn line_items_table zone from the source doc OCR.
// Returns { ok, columns: ProposedColumn[], reason? }.
export async function getProposedColumns(templateId, zoneId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/zones/${zoneId}/proposed-columns`;
  return (await axios.get(url)).data;
}

// Saved columns for a zone. Returns { ok, columns: row[] }.
export async function getSavedColumns(templateId, zoneId) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/zones/${zoneId}/columns`;
  return (await axios.get(url)).data;
}

// Replace-all save of confirmed columns. Returns { ok, columns } or 400 { ok:false, error }.
export async function saveColumns(templateId, zoneId, columns) {
  const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${templateId}/zones/${zoneId}/columns`;
  return (await axios.put(url, { columns })).data;
}
